import { Flex, freddyTheme, Grid, GridItem, Table, TableHead } from '@coolblue-development/freddy';
import React, { useState } from 'react';

import { MatrixCheckboxCell } from './MatrixCheckboxCell';
import { MatrixValuesType } from './MatrixHelper';

type Props = {
  matrixValues: MatrixValuesType;
  setMatrixValues: React.Dispatch<React.SetStateAction<MatrixValuesType>>;
  columnLabels: string[];
  rowLabels: string[];
  firstRowSelectsAll?: boolean;
};

export const MatrixTable = ({
  matrixValues,
  setMatrixValues,
  columnLabels,
  rowLabels,
  firstRowSelectsAll
}: Props) => {
  const columnTemplate = `65px repeat(${columnLabels.length}, minmax(calc((100% - 65px) / ${columnLabels.length}), 1fr))`;
  const [hoveredState, setHoveredState] = useState<{ column?: number; row?: number }>({
    column: undefined,
    row: undefined
  });

  const getMatrixClassCombinations = (
    rowIndex: number | undefined,
    columnIndex: number | undefined
  ) => {
    if (rowIndex === undefined || columnIndex === undefined) return '';
    const classCombinations = [];

    for (let i = 0; i <= rowIndex; i++) {
      classCombinations.push(`:where(.matrix-row-${i}):where(.matrix-column-${columnIndex})`);
    }
    for (let i = 0; i <= columnIndex; i++) {
      classCombinations.push(`:where(.matrix-column-${i}):where(.matrix-row-${rowIndex})`);
    }
    return classCombinations.join(', ');
  };

  return (
    <Table
      layout='fixed'
      sx={{
        width: 'auto',
        maxWidth: '100%',
        [`*:where(${getMatrixClassCombinations(hoveredState.row, hoveredState.column)})`]: {
          backgroundColor: freddyTheme.color.background.interactiveHovered
        }
      }}
    >
      <TableHead>
        <Grid component='tr' templateColumns={columnTemplate}>
          {columnLabels.map((label, index) => (
            <GridItem
              component='th'
              scope='column'
              key={label}
              column={index + 2}
              px='0'
              sx={{ fontWeight: freddyTheme.typography.fontWeight.openSans.semiBold }}
              className={`matrix-column-${index + 1} matrix-row-0`}
            >
              {label}
            </GridItem>
          ))}
        </Grid>
      </TableHead>

      <Flex component='tbody' direction='column'>
        {rowLabels.map((label, index) => (
          <Grid
            component='tr'
            key={label}
            templateColumns={columnTemplate}
            sx={{
              borderBottom:
                index === 0 && firstRowSelectsAll
                  ? `${freddyTheme.borderWidth[25]} solid ${freddyTheme.color.border.interactiveBase}`
                  : undefined
            }}
          >
            <GridItem
              component='th'
              scope='row'
              key={label}
              column={1}
              sx={{ fontWeight: 'normal', textAlign: 'left' }}
              className={`matrix-column-0 matrix-row-${index + 1}`}
            >
              {label}
            </GridItem>
            {columnLabels.map((columnLabel, columnIndex) => {
              const isFirstRow = index === 0;

              return (
                <MatrixCheckboxCell
                  key={`${columnLabel}-${label}`}
                  className={`matrix-row-${index + 1} matrix-column-${columnIndex + 1}`}
                  columnLabel={columnLabel}
                  label={label}
                  columnIndex={columnIndex}
                  matrixValues={matrixValues}
                  setMatrixValues={setMatrixValues}
                  selectAllConfig={
                    isFirstRow && firstRowSelectsAll
                      ? {
                          checkedCount: Array.from(
                            matrixValues.get(columnLabel)?.values() || []
                          ).filter(Boolean).length,
                          totalCount: rowLabels.length - 1
                        }
                      : undefined
                  }
                  onHover={hoverActive => {
                    setHoveredState({
                      column: hoverActive ? columnIndex + 1 : undefined,
                      row: hoverActive ? index + 1 : undefined
                    });
                  }}
                />
              );
            })}
          </Grid>
        ))}
      </Flex>
    </Table>
  );
};
